// SendMessageModal — JJ 2026-05-19
// Modal d'envoi SMS+Email depuis fiche dossier (pattern shadcn Dialog).
//
// Sources templates :
//  - SMS  → GET /api/communication/templates?channel=sms&status=active (collection `comm_templates`)
//  - Email → GET /api/governance/email-config/templates?tenant_id=... (collection `email_templates`)
//
// Endpoint d'envoi unifié :
//  - POST /api/communication/send {channel, template_id, recipient, variables, triggered_by}
//
// La fallback côté backend (communication/service.py:get_template) résout
// les keys `email_templates` quand pas trouvées dans `comm_templates`.
import { useState, useEffect, useMemo } from "react";
import { Send, Loader2, Mail, MessageSquare } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import { API, getAuthHeaders } from "./helpers";

// Normalise un design_templates email (Design Studio) vers le shape commun.
// Design Studio doc shape : {_id, tenant_id, type: "email_template", nom,
//   description, audience: "client"|"installateur", categorie, contenu:
//   {subject, html_body, text_body, variables: [...]}, actif: true, ...}
function normalizeDesignEmailTemplate(t) {
  const contenu = t.contenu || {};
  return {
    id: t.id || (t._id ? String(t._id) : ""),
    name: t.nom || t.name || "",
    description: t.description || "",
    audience: (t.audience || "").toLowerCase(),
    category: (t.categorie || t.category || "").toLowerCase(),
    channel: "email",
    content: contenu.html_body || contenu.text_body || "",
    subject: contenu.subject || "",
    variables: (contenu.variables || []).map((v) => ({
      name: v.key || v.name,
      required: v.required || false,
      description: v.description || "",
      default_value: v.default ?? v.default_value ?? null,
    })),
  };
}

// Détecte un destinataire fictif (preprod anonymisé) — bloque l'envoi
function isFakeRecipient(value) {
  if (!value) return false;
  if (typeof value !== "string") return false;
  return value.endsWith("@preprod.local") || value.startsWith("+33900");
}

// `audienceFilter` : liste des audiences acceptées. Default `["client"]`
// (cas par défaut : fiche dossier d'un particulier).
// Sur la page gestion installateurs, passer `["installateur"]`.
// Pour un contexte mixte (rare), passer `["client", "installateur"]`.
export default function SendMessageModal({
  open, onClose, dossier, tenantId, currentUser,
  audienceFilter = ["client"],
}) {
  const [channel, setChannel] = useState("sms");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [recipient, setRecipient] = useState("");
  const [variables, setVariables] = useState({});
  const [sending, setSending] = useState(false);

  const client = dossier?.client || {};
  const defaultPhone = client.telephone || dossier?.telephone || "";
  const defaultEmail = client.email || dossier?.email || "";

  // Load templates pour le canal courant (2 sources distinctes)
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // SMS  → comm_templates (collection unifiée, V1 — migration Design Studio en V1.1)
    // Email → design_templates (Design Studio, type=email_template) — JJ 2026-05-19
    //   Les templates governance/email_config (Gamora, AG-04, etc.) sont
    //   internes/système et ne sont PAS exposés ici.
    const url =
      channel === "sms"
        ? `${API}/api/communication/templates?channel=sms&status=active`
        : `${API}/api/design/templates?type=email_template`;

    fetch(url, { headers: getAuthHeaders(tenantId), credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        let list = [];
        if (channel === "sms") {
          list = data?.templates || [];
        } else {
          // Design Studio retourne directement un array (pas {templates: []})
          const raw = Array.isArray(data) ? data : (data?.templates || []);
          const audienceSet = new Set(audienceFilter.map((a) => a.toLowerCase()));
          list = raw
            .filter((t) => t.actif !== false && t.active !== false)
            .map(normalizeDesignEmailTemplate)
            .filter((t) => !t.audience || audienceSet.has(t.audience));
        }
        setTemplates(list);
        setSelectedTemplateId(list[0]?.id || "");
      })
      .catch((e) => toast.error(`Templates : ${e.message || e}`))
      .finally(() => setLoading(false));
  }, [open, channel, tenantId, audienceFilter]);

  useEffect(() => {
    setRecipient(channel === "sms" ? defaultPhone : defaultEmail);
  }, [channel, defaultPhone, defaultEmail]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId),
    [templates, selectedTemplateId]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    const vars = {};
    for (const v of selectedTemplate.variables || []) {
      const key = v.name || v;
      const map = {
        prenom: client.prenom || "",
        nom: client.nom || "",
        reference: dossier?.reference || "",
        salutation: client.prenom ? `${client.prenom} ${client.nom || ""}`.trim() : (client.nom || ""),
        client_nom: client.nom || "",
        date: "",
        heure: "",
      };
      vars[key] = map[key] ?? v.default_value ?? "";
    }
    setVariables(vars);
  }, [selectedTemplate, client.prenom, client.nom, dossier?.reference]);

  const fakeRecipient = isFakeRecipient(recipient);

  const handleSend = async () => {
    if (!selectedTemplateId) {
      toast.error("Sélectionnez un template");
      return;
    }
    if (!recipient) {
      toast.error("Destinataire manquant");
      return;
    }
    if (fakeRecipient) {
      toast.error(`${channel === "sms" ? "Téléphone" : "Email"} fictif (preprod anonymisé) — envoi bloqué`);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/communication/send`, {
        method: "POST",
        headers: { ...getAuthHeaders(tenantId), "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          channel,
          template_id: selectedTemplateId,
          recipient,
          variables,
          triggered_by: `human:${currentUser?.email || "support"}`,
          context: `dossier:${dossier?.id || dossier?._id || ""}`,
        }),
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        toast.success(`${channel === "sms" ? "SMS" : "Email"} envoyé à ${recipient}`);
        onClose();
      } else {
        toast.error(`Échec : ${data?.error || `HTTP ${res.status}`}`);
      }
    } catch (e) {
      toast.error(`Erreur : ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg"
        style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}
        data-testid="send-message-modal"
      >
        <DialogHeader>
          <DialogTitle style={{ color: "var(--oh-text)" }} className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[var(--oh-accent-text)]" />
            Envoyer un message
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Tabs canal */}
          <div className="flex gap-2">
            {[
              { id: "sms", label: "SMS", Icon: MessageSquare },
              { id: "email", label: "Email", Icon: Mail },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`channel-${id}-tab`}
                onClick={() => setChannel(id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors"
                style={{
                  background: channel === id ? "rgba(59,130,246,0.15)" : "transparent",
                  color: channel === id ? "#60a5fa" : "var(--oh-text-muted)",
                  border: `1px solid ${channel === id ? "rgba(59,130,246,0.3)" : "var(--oh-border)"}`,
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Template select */}
          <div>
            <label className="block text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
              Template ({templates.length} disponible{templates.length > 1 ? "s" : ""})
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-xs" style={{ color: "var(--oh-text-muted)" }}>
                <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
              </div>
            ) : templates.length === 0 ? (
              <div className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Aucun template {channel === "sms" ? "SMS" : "Email"} actif.{" "}
                {channel === "sms"
                  ? "Créez-en un dans Hub Communication."
                  : "Créez-en un dans Marketing → Design Studio (type Template email, catégorie client)."}
              </div>
            ) : (
              <select
                data-testid="template-select"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-2 py-1.5 text-xs rounded"
                style={{
                  background: "var(--oh-bg-elevated)",
                  color: "var(--oh-text)",
                  border: "1px solid var(--oh-border)",
                }}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.description ? `— ${t.description}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Recipient */}
          <div>
            <label className="block text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
              Destinataire ({channel === "sms" ? "Téléphone" : "Email"})
            </label>
            <input
              data-testid="recipient-input"
              type={channel === "email" ? "email" : "tel"}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={channel === "sms" ? "+33612345678" : "client@example.com"}
              className="w-full px-2 py-1.5 text-xs rounded"
              style={{
                background: "var(--oh-bg-elevated)",
                color: "var(--oh-text)",
                border: `1px solid ${fakeRecipient ? "rgba(248,113,113,0.5)" : "var(--oh-border)"}`,
              }}
            />
            {fakeRecipient && (
              <div
                data-testid="fake-recipient-warning"
                className="mt-1 text-xs px-2 py-1 rounded"
                style={{ background: "rgba(248,113,113,0.1)", color: "#f87171", border: "1px solid rgba(248,113,113,0.3)" }}
              >
                {channel === "sms" ? "Téléphone fictif" : "Email fictif"} (preprod anonymisé) — envoi bloqué
              </div>
            )}
          </div>

          {/* Variables */}
          {selectedTemplate?.variables?.length > 0 && (
            <div>
              <label className="block text-xs mb-1" style={{ color: "var(--oh-text-muted)" }}>
                Variables
              </label>
              <div className="space-y-2">
                {selectedTemplate.variables.map((v) => {
                  const key = v.name || v;
                  return (
                    <div key={key} className="flex items-center gap-2">
                      <span
                        className="text-xs w-24 truncate"
                        style={{ color: "var(--oh-text-muted)" }}
                        title={v.description || key}
                      >
                        {key}
                        {v.required ? " *" : ""}
                      </span>
                      <input
                        data-testid={`var-${key}`}
                        value={variables[key] || ""}
                        onChange={(e) => setVariables({ ...variables, [key]: e.target.value })}
                        className="flex-1 px-2 py-1 text-xs rounded"
                        style={{
                          background: "var(--oh-bg-elevated)",
                          color: "var(--oh-text)",
                          border: "1px solid var(--oh-border)",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Aperçu */}
          {selectedTemplate?.content && (
            <div
              className="p-2 rounded text-xs max-h-32 overflow-y-auto"
              style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text-muted)" }}
            >
              <div className="font-medium mb-1" style={{ color: "var(--oh-text)" }}>
                Aperçu {channel === "email" && selectedTemplate.subject ? `· ${selectedTemplate.subject}` : ""}
              </div>
              <pre className="whitespace-pre-wrap font-sans">
                {channel === "email"
                  ? selectedTemplate.content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 300) + (selectedTemplate.content.length > 300 ? "…" : "")
                  : selectedTemplate.content}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            Annuler
          </Button>
          <Button
            data-testid="send-btn"
            onClick={handleSend}
            disabled={sending || !selectedTemplateId || !recipient || fakeRecipient}
            className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-1" />
            )}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
